/**
 * Owner report / Tenant report / Property report — the "master report" for
 * one account or one property: every ownership/lease/charge/payment/notice
 * event touching it, chronological, newest first. Three tabs, one shared
 * implementation (`AccountTimeline`) below, since they're parametrically the
 * same feature — unlike the other six reports, which each have a genuinely
 * different row shape and filter set, these differ only in which id they key
 * on and which endpoint they call. The property report also carries fee and
 * maintenance events plus a totals card (`PropertySummaryCard`).
 *
 * Unlike every other tab, the owner/tenant here is required, not an
 * optional narrowing filter — there's no portfolio-wide version of "this
 * one account's history" to show before you pick someone. Nothing is
 * fetched until a subject is selected.
 */

import { useEffect, useState } from 'react';
import { useList } from '@refinedev/core';
import { api } from '../../../lib/api.js';
import { DataTable, type Column } from '../../../components/DataTable.js';
import { ReportFilterBar } from '../../../components/ReportFilterBar.js';
import { StatGrid, type StatTone } from '../../../components/StatGrid.js';
import { TrendChart, type TrendPoint } from '../../../components/charts/TrendChart.js';
import { humanizeEnum } from '../../../components/Form.js';

interface TimelineEvent {
  eventDate: string;
  category: string;
  title: string;
  description: string;
  amount: string | null;
  propertyName: string | null;
  unitLabel: string | null;
  leaseId: string | null;
}

const columns: Column<TimelineEvent>[] = [
  { key: 'date', header: 'Date', render: (r) => r.eventDate.slice(0, 10) },
  {
    key: 'category',
    header: 'Category',
    render: (r) => <span className="pill">{humanizeEnum(r.category)}</span>,
  },
  { key: 'title', header: 'Event', render: (r) => r.title },
  { key: 'description', header: 'Details', render: (r) => r.description },
  {
    key: 'amount',
    header: 'Amount',
    numeric: true,
    render: (r) => (r.amount ? `$${r.amount}` : '—'),
  },
  {
    key: 'property',
    header: 'Property / unit',
    render: (r) => [r.propertyName, r.unitLabel].filter(Boolean).join(' — ') || '—',
  },
];

function money(value: number): string {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

const DAY_MS = 86_400_000;

/** "Payment received" events, summed into day/week buckets spanning the
 *  earliest to latest payment actually in the timeline — there's no fixed
 *  report window to bucket against here the way Deposits collected has one,
 *  since a master report defaults to a subject's whole history.
 *
 *  Label formatting is pinned to UTC (`timeZone: 'UTC'` below) because the
 *  server sends date-only columns as UTC midnight — `toLocaleDateString`'s
 *  default local-timezone rendering would print the day *before* the actual
 *  event for every viewer west of UTC, which is every viewer this app has
 *  (metro Detroit). The bucket math itself (`Date.getTime()` diffs) doesn't
 *  need this — only display formatting does. */
function buildPaymentTrend(events: TimelineEvent[]): TrendPoint[] {
  const payments = events
    .filter((e) => e.category === 'payment' && e.title === 'Payment received' && e.amount)
    .map((e) => ({ date: new Date(e.eventDate), value: Number(e.amount) }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  if (payments.length === 0) return [];

  const start = payments[0]!.date;
  const end = payments[payments.length - 1]!.date;
  const totalDays = Math.max(1, Math.round((end.getTime() - start.getTime()) / DAY_MS) + 1);
  const bucketDays = totalDays > 45 ? 7 : 1;
  const bucketCount = Math.max(1, Math.ceil(totalDays / bucketDays));

  const sums = new Map<number, number>();
  for (const payment of payments) {
    const dayOffset = Math.round((payment.date.getTime() - start.getTime()) / DAY_MS);
    const index = Math.min(bucketCount - 1, Math.max(0, Math.floor(dayOffset / bucketDays)));
    sums.set(index, (sums.get(index) ?? 0) + payment.value);
  }

  return Array.from({ length: bucketCount }, (_, i) => {
    const bucketStart = new Date(start.getTime() + i * bucketDays * DAY_MS);
    const value = sums.get(i) ?? 0;
    const dateLabel = bucketStart.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
    return {
      label: bucketDays === 1 ? dateLabel : `Week of ${dateLabel}`,
      value,
      displayValue: money(value),
    };
  });
}

type SummaryBucket =
  | 'chargesBilled'
  | 'feesCharged'
  | 'paymentsCollected'
  | 'paymentsPending'
  | 'paymentsReturned'
  | 'processingFees'
  | 'managementFees'
  | 'ownerIncome'
  | 'maintenanceCost';

interface SummaryItem {
  date: string;
  name: string;
  detail: string;
  amount: string;
}

interface PropertyReportSummary {
  propertyName: string;
  address: string;
  currentOwners: string | null;
  totals: Record<SummaryBucket, string>;
  items: Record<SummaryBucket, SummaryItem[]>;
}

/** Tile order, labels, hints, and each category's own color. `bucket`
 *  keys both the total and the item list the server sends for it. */
const SUMMARY_TILES: {
  bucket: SummaryBucket;
  label: string;
  hint?: string;
  tone: StatTone;
}[] = [
  { bucket: 'chargesBilled', label: 'Charges billed', hint: 'Excludes voided', tone: 'blue' },
  { bucket: 'feesCharged', label: 'Late & NSF fees', hint: 'Charged to tenants', tone: 'amber' },
  { bucket: 'paymentsCollected', label: 'Payments collected', hint: 'Settled', tone: 'green' },
  { bucket: 'paymentsPending', label: 'Payments pending', tone: 'teal' },
  { bucket: 'paymentsReturned', label: 'Payments returned', tone: 'red' },
  { bucket: 'processingFees', label: 'Processing fees', hint: 'Stripe ACH', tone: 'purple' },
  { bucket: 'managementFees', label: 'Management fees', tone: 'slate' },
  { bucket: 'ownerIncome', label: 'Owner income', hint: 'On owner statements', tone: 'pink' },
  { bucket: 'maintenanceCost', label: 'Maintenance cost', tone: 'orange' },
];

const summaryItemColumns: Column<SummaryItem>[] = [
  { key: 'date', header: 'Date', render: (r) => r.date },
  { key: 'name', header: 'Name', render: (r) => r.name },
  { key: 'detail', header: 'Details', render: (r) => r.detail },
  { key: 'amount', header: 'Amount', numeric: true, render: (r) => `$${r.amount}` },
];

/** The property report's totals. Every figure is the server's own decimal
 *  string, rendered verbatim — nothing here is summed in the browser. Plain
 *  `api()` + `useEffect`, same as `ReportsGlanceWidget`: one object, not a
 *  list. */
function PropertySummaryCard({
  propertyId,
  from,
  to,
}: {
  propertyId: string;
  from: string;
  to: string;
}) {
  const [summary, setSummary] = useState<PropertyReportSummary | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    setSummary(null);
    setError(null);
    const search = new URLSearchParams({ propertyId });
    if (from) search.set('from', from);
    if (to) search.set('to', to);
    api<PropertyReportSummary>(`/reports/property/summary?${search.toString()}`)
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId, from, to]);

  if (error) {
    return (
      <p className="alert alert--error">
        {error instanceof Error ? error.message : 'Could not load the property summary.'}
      </p>
    );
  }
  if (!summary) return <p className="muted">Loading summary…</p>;

  return (
    <div className="card stack">
      <div>
        <h2>{summary.propertyName}</h2>
        <p className="muted">
          {summary.address}
          {summary.currentOwners ? ` · Owned by ${summary.currentOwners}` : ''}
        </p>
      </div>
      <StatGrid
        variant="tile"
        stats={SUMMARY_TILES.map((tile) => ({
          label: tile.label,
          hint: tile.hint,
          tone: tile.tone,
          value: `$${summary.totals[tile.bucket]}`,
          details: (
            <DataTable
              columns={summaryItemColumns}
              rows={summary.items[tile.bucket]}
              rowKey={(row) => `${row.date}-${row.name}-${row.detail}-${row.amount}`}
              emptyMessage="Nothing in this period."
            />
          ),
        }))}
      />
    </div>
  );
}

const SUBJECT = {
  owner: { resource: 'reports/owner', idField: 'ownerId', article: 'an owner' },
  tenant: { resource: 'reports/tenant', idField: 'tenantId', article: 'a tenant' },
  property: { resource: 'reports/property', idField: 'propertyId', article: 'a property' },
} as const;

function AccountTimeline({
  kind,
  subjectId,
  onSubjectChange,
}: {
  kind: 'owner' | 'tenant' | 'property';
  subjectId: string;
  onSubjectChange: (value: string) => void;
}) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { resource, idField, article } = SUBJECT[kind];

  const { data, isLoading, error } = useList<TimelineEvent>({
    resource,
    pagination: { mode: 'off' },
    filters: [
      { field: idField, operator: 'eq', value: subjectId || undefined },
      { field: 'from', operator: 'eq', value: from || undefined },
      { field: 'to', operator: 'eq', value: to || undefined },
    ],
    queryOptions: { enabled: subjectId !== '' },
  });

  const rows = data?.data ?? [];
  const trend = buildPaymentTrend(rows);

  return (
    <div className="stack">
      {kind === 'property' ? (
        <p className="muted">
          Everything on this property, newest first — owners, leases, charges and fees billed to
          tenants, payments and returns, Stripe processing and management fees, owner statement
          income, and work order costs.
        </p>
      ) : (
        <p className="muted">
          Every ownership, lease, charge, payment, and notice event on this account, newest first —
          when a{' '}
          {kind === 'owner'
            ? 'property was added to their portfolio'
            : 'lease was added to their name'}
          , when money moved, and what else happened in between.
          {kind === 'owner' ? (
            <>
              {' '}
              A disbursement isn't money in the owner's bank until it reads "Disbursement paid" or
              "Settled externally" — "Transfer completed" means it is still in their Stripe balance.
            </>
          ) : null}
        </p>
      )}

      <ReportFilterBar
        {...(kind === 'owner'
          ? { ownerId: subjectId, onOwnerChange: onSubjectChange }
          : kind === 'tenant'
            ? { tenantId: subjectId, onTenantChange: onSubjectChange }
            : {
                propertyId: subjectId,
                onPropertyChange: onSubjectChange,
                propertyEmptyLabel: 'Select a property',
              })}
        exportPath={`/${resource}`}
        exportParams={{
          [idField]: subjectId || undefined,
          from: from || undefined,
          to: to || undefined,
        }}
      >
        <label className="row">
          <span className="muted">From</span>
          <input
            className="field__input"
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
        </label>
        <label className="row">
          <span className="muted">To</span>
          <input
            className="field__input"
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
        </label>
      </ReportFilterBar>

      {subjectId === '' ? (
        <p className="muted">Select {article} above to generate the report.</p>
      ) : (
        <>
          {kind === 'property' ? (
            <PropertySummaryCard propertyId={subjectId} from={from} to={to} />
          ) : null}

          {!isLoading && !error && trend.length > 0 ? (
            <div className="card">
              <p className="muted">
                Payments received over this {kind === 'property' ? 'property' : 'account'}'s
                history.
              </p>
              <TrendChart points={trend} colorVar="--accent" />
            </div>
          ) : null}

          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(row) =>
              `${row.category}-${row.eventDate}-${row.leaseId}-${row.title}-${row.amount}`
            }
            isLoading={isLoading}
            error={error}
            emptyMessage="No activity recorded yet."
          />
        </>
      )}
    </div>
  );
}

export function OwnerReport({
  ownerId,
  onOwnerChange,
}: {
  ownerId: string;
  onOwnerChange: (value: string) => void;
}) {
  return <AccountTimeline kind="owner" subjectId={ownerId} onSubjectChange={onOwnerChange} />;
}

export function TenantReport({
  tenantId,
  onTenantChange,
}: {
  tenantId: string;
  onTenantChange: (value: string) => void;
}) {
  return <AccountTimeline kind="tenant" subjectId={tenantId} onSubjectChange={onTenantChange} />;
}

export function PropertyReport({
  propertyId,
  onPropertyChange,
}: {
  propertyId: string;
  onPropertyChange: (value: string) => void;
}) {
  return (
    <AccountTimeline kind="property" subjectId={propertyId} onSubjectChange={onPropertyChange} />
  );
}
